import {Alert, Checkbox, Modal, Spin, Typography} from "antd";
import {useEffect, useMemo, useState} from "react";
import ApiService from "../../../services/ApiService";

// Bir izin kaldirildiginda bagimli izinler de dusurulur.
// Ornegin Library Model Automation (run.lma) kapatilirsa Check altindaki
// WSEL Library de cekilir cunku ona bagli verileri uretiyor.
const REMOVAL_CASCADE = {
    'run.lma': ['check.wsel_library'],
};

function applyRemovalCascade(set, removedKey) {
    const dependents = REMOVAL_CASCADE[removedKey];
    if (!dependents) return;
    for (const dep of dependents) {
        set.delete(dep);
    }
}

export default function UserPermissionsModal({user, visible, onClose, onSaved}) {
    const [catalog, setCatalog] = useState([]);
    const [granted, setGranted] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (!visible || !user?.id) return;
            setLoading(true);
            try {
                const [catalogRes, permsRes] = await Promise.all([
                    ApiService.AdminDashboard.GetModuleCatalog(),
                    ApiService.AdminDashboard.GetUserPermissions(user.id),
                ]);
                if (catalogRes?.success) setCatalog(catalogRes.data || []);
                if (permsRes?.success) setGranted(new Set(permsRes.data || []));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [visible, user?.id]);

    const grouped = useMemo(() => {
        const out = {Run: [], Check: [], Comparison: []};
        for (const m of catalog) {
            if (out[m.group]) out[m.group].push(m);
        }
        return out;
    }, [catalog]);

    // catalog'daki Parent alanindan parent->children haritasi.
    // Bir parent isaretlendiginde tum cocuklari otomatik aciliyor.
    const childrenByParent = useMemo(() => {
        const map = {};
        for (const m of catalog) {
            if (!m.parent) continue;
            if (!map[m.parent]) map[m.parent] = [];
            map[m.parent].push(m.key);
        }
        return map;
    }, [catalog]);

    const applyAddCascade = (set, addedKey) => {
        const children = childrenByParent[addedKey];
        if (!children) return;
        for (const c of children) set.add(c);
    };

    const toggle = (key) => {
        const next = new Set(granted);
        if (next.has(key)) {
            next.delete(key);
            applyRemovalCascade(next, key);
        } else {
            next.add(key);
            applyAddCascade(next, key);
        }
        setGranted(next);
    };

    const setGroup = (groupItems, value) => {
        const next = new Set(granted);
        for (const m of groupItems) {
            if (value) {
                next.add(m.key);
                applyAddCascade(next, m.key);
            } else {
                next.delete(m.key);
                applyRemovalCascade(next, m.key);
            }
        }
        setGranted(next);
    };

    const onOk = async () => {
        setSaving(true);
        try {
            const res = await ApiService.AdminDashboard.SetUserPermissions({
                userId: user.id,
                moduleKeys: Array.from(granted),
            });
            if (res?.success) {
                onSaved && onSaved();
                onClose && onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    const renderGroup = (title, items) => {
        if (!items || items.length === 0) return null;
        const allChecked = items.every((m) => granted.has(m.key));
        const someChecked = items.some((m) => granted.has(m.key));
        return (
            <div key={title} style={{marginBottom: 16}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
                    <Checkbox
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) => setGroup(items, e.target.checked)}
                    >
                        <Typography.Text strong>{title}</Typography.Text>
                    </Checkbox>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4, paddingLeft: 24}}>
                    {items.map((m) => (
                        <Checkbox
                            key={m.key}
                            checked={granted.has(m.key)}
                            onChange={() => toggle(m.key)}
                        >
                            {m.title}
                        </Checkbox>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={`Permissions: ${user?.email || ''}`}
            open={visible}
            onOk={onOk}
            onCancel={onClose}
            confirmLoading={saving}
            okText={'Save'}
            width={640}
            destroyOnClose={true}
        >
            <Spin spinning={loading}>
                {user?.isAdmin && (
                    <Alert
                        type={'info'}
                        showIcon
                        message={'This user is an administrator. Admins have access to all modules regardless of these settings.'}
                        style={{marginBottom: 12}}
                    />
                )}
                {renderGroup('Run', grouped.Run)}
                {renderGroup('Check', grouped.Check)}
                {renderGroup('Comparison', grouped.Comparison)}
            </Spin>
        </Modal>
    );
}
